import { FileTextOutlined, PlusOutlined } from '@ant-design/icons';
import {
  App,
  Button,
  Card,
  DatePicker,
  Input,
  Select,
  Space,
  Table,
  Tooltip,
  Typography,
  type TableColumnsType,
} from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Route, Routes, useNavigate } from 'react-router';

import { useClientsGetClient } from '@/api/generated/endpoints/clients/clients';
import { useShipmentsListShipments } from '@/api/generated/endpoints/shipments/shipments';
import type { ShipmentListItem, ShipmentStatus } from '@/api/generated/model';
import { useHasRole } from '@/auth/useHasRole';
import { ClientSelect } from '@/components/ClientSelect';
import { QtyCell } from '@/components/qty';
import { ResolutionPopover } from '@/components/ResolutionPopover';
import { ShipmentStatusTag } from '@/components/StatusTag';
import { ShipmentPage } from '@/pages/shipments/ShipmentPage';
import { DraftEditor } from '@/pages/shipments/DraftEditor';
import { compareDecimals } from '@/lib/decimal';
import { SHIPMENT_STATUS_COLOR, statusFilters } from '@/lib/statuses';
import { useIsMobile } from '@/lib/useIsMobile';
import { useTableState } from '@/lib/useTableState';

const DATE = 'DD.MM.YYYY';
const API_DATE = 'YYYY-MM-DD';

type Filter =
  | 'q'
  | 'client_id'
  | 'delivery_point_id'
  | 'status'
  | 'created_from'
  | 'created_to'
  | 'shipped_from'
  | 'shipped_to';

const FILTERS: readonly Filter[] = [
  'q',
  'client_id',
  'delivery_point_id',
  'status',
  'created_from',
  'created_to',
  'shipped_from',
  'shipped_to',
];

/** Раздел «Отгрузки»: список и карточка /shipments/:id (день 29). */
export function ShipmentsSection() {
  return (
    <Routes>
      <Route index element={<ShipmentsPage />} />
      <Route path="new" element={<NewShipmentPage />} />
      <Route path=":shipmentId" element={<ShipmentPage />} />
    </Routes>
  );
}

/** API отдаёт ноль как «0.000» — сравниваем числа, а не строки. */
const isNonZero = (v: string) => compareDecimals(v, '0') !== 0;

function NewShipmentPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { message } = App.useApp();
  return (
    <Card title={t('shipment.new')}>
      <DraftEditor
        onSaved={(s) => {
          void message.success(t('shipment.createdDone', { number: s.number }));
          void navigate(`../${s.id}`, { replace: true });
        }}
        onCancel={() => {
          void navigate('..');
        }}
      />
    </Card>
  );
}

function intParam(v: string | undefined): number | null {
  const n = v === undefined ? NaN : Number.parseInt(v, 10);
  return Number.isInteger(n) ? n : null;
}

function range(from: string | undefined, to: string | undefined): [Dayjs, Dayjs] | null {
  return from && to ? [dayjs(from), dayjs(to)] : null;
}

function ShipmentsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const table = useTableState<Filter>({ filters: FILTERS, defaultSort: '-created_at' });
  const canCreate = useHasRole('storekeeper');
  const f = table.filters;
  const [search, setSearch] = useState(f.q[0] ?? '');
  const clientId = intParam(f.client_id[0]);
  const pointId = intParam(f.delivery_point_id[0]);
  const client = useClientsGetClient(clientId ?? 0, { query: { enabled: clientId !== null } });

  const list = useShipmentsListShipments({
    ...table.query,
    q: f.q[0] ?? null,
    client_id: clientId,
    delivery_point_id: pointId,
    status: f.status.length ? (f.status as ShipmentStatus[]) : null,
    created_from: f.created_from[0] ?? null,
    created_to: f.created_to[0] ?? null,
    shipped_from: f.shipped_from[0] ?? null,
    shipped_to: f.shipped_to[0] ?? null,
  });

  function setRange(kind: 'created' | 'shipped', value: [Dayjs, Dayjs] | null) {
    table.setFilter(`${kind}_from`, value ? value[0].format(API_DATE) : null);
    table.setFilter(`${kind}_to`, value ? value[1].format(API_DATE) : null);
  }

  const mobile = useIsMobile();
  // Телефон (T-176): одна колонка-карточка; фильтр по статусу — в её заголовке.
  const mobileColumns: TableColumnsType<ShipmentListItem> = [
    {
      key: 'status',
      title: t('shipments.colNumber'),
      filters: statusFilters(t, 'shipment', SHIPMENT_STATUS_COLOR),
      filteredValue: f.status.length ? f.status : null,
      render: (_: unknown, s) => (
        <Space direction="vertical" size={2} style={{ width: '100%' }}>
          <Space size={4} wrap>
            <a>{s.number}</a>
            <ShipmentStatusTag status={s.status} clientName={s.client.brand ?? s.client.name} />
            {s.resolution && <ResolutionPopover resolution={s.resolution} />}
          </Space>
          <Typography.Text>
            {s.delivery_point
              ? `${s.client.brand ?? s.client.name} → ${s.delivery_point.name}`
              : (s.client.brand ?? s.client.name)}
          </Typography.Text>
          <Space size={8} wrap>
            {isNonZero(s.total_kg) && <QtyCell value={s.total_kg} uom="kg" />}
            {isNonZero(s.total_pcs) && <QtyCell value={s.total_pcs} uom="pcs" />}
            {isNonZero(s.total_box) && <QtyCell value={s.total_box} uom="box" />}
            <Typography.Text type="secondary">
              {dayjs(s.shipped_at ?? s.created_at).format(DATE)}
            </Typography.Text>
          </Space>
        </Space>
      ),
    },
  ];

  const columns: TableColumnsType<ShipmentListItem> = [
    {
      key: 'number',
      title: t('shipments.colNumber'),
      dataIndex: 'number',
      width: 170,
      sorter: true,
      sortOrder: table.sortOrderFor('number'),
      render: (n: string, s) => (
        <Space size={4}>
          <a>{n}</a>
          {s.upd_document_id !== null && (
            <Tooltip title={t('shipments.fromUpd')}>
              <FileTextOutlined style={{ color: '#8c8c8c' }} />
            </Tooltip>
          )}
        </Space>
      ),
    },
    {
      key: 'client',
      title: t('shipments.colClient'),
      render: (_: unknown, s) => {
        const name = s.client.brand ?? s.client.name;
        return s.delivery_point ? `${name} → ${s.delivery_point.name}` : name;
      },
    },
    {
      key: 'status',
      title: t('shipments.colStatus'),
      width: 300,
      filters: statusFilters(t, 'shipment', SHIPMENT_STATUS_COLOR),
      filteredValue: f.status.length ? f.status : null,
      render: (_: unknown, s) => (
        <Space size={4} wrap>
          <ShipmentStatusTag status={s.status} clientName={s.client.brand ?? s.client.name} />
          {s.resolution && <ResolutionPopover resolution={s.resolution} />}
        </Space>
      ),
    },
    { key: 'lines', title: t('shipments.colLines'), dataIndex: 'lines', width: 70 },
    {
      key: 'total',
      title: t('shipments.colTotal'),
      width: 170,
      align: 'right',
      render: (_: unknown, s) => (
        <Space direction="vertical" size={0} style={{ alignItems: 'flex-end' }}>
          {isNonZero(s.total_kg) && <QtyCell value={s.total_kg} uom="kg" />}
          {isNonZero(s.total_pcs) && <QtyCell value={s.total_pcs} uom="pcs" />}
          {isNonZero(s.total_box) && <QtyCell value={s.total_box} uom="box" />}
        </Space>
      ),
    },
    {
      key: 'created_at',
      title: t('shipments.colCreated'),
      dataIndex: 'created_at',
      width: 120,
      sorter: true,
      sortOrder: table.sortOrderFor('created_at'),
      render: (d: string) => dayjs(d).format(DATE),
    },
    {
      key: 'shipped_at',
      title: t('shipments.colShipped'),
      dataIndex: 'shipped_at',
      width: 120,
      sorter: true,
      sortOrder: table.sortOrderFor('shipped_at'),
      render: (d: string | null) => (d ? dayjs(d).format(DATE) : '—'),
    },
  ];

  return (
    <Card
      title={t('shipments.title')}
      extra={
        canCreate && (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              void navigate('new');
            }}
          >
            {t('shipment.create')}
          </Button>
        )
      }
    >
      <Space
        wrap
        direction={mobile ? 'vertical' : 'horizontal'}
        style={{ marginBottom: 12, width: mobile ? '100%' : undefined }}
      >
        <Input.Search
          allowClear
          placeholder={t('shipments.searchPlaceholder')}
          style={{ width: mobile ? '100%' : 180 }}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            if (e.target.value === '') table.setFilter('q', null);
          }}
          onSearch={(q) => {
            table.setFilter('q', q.trim() || null);
          }}
        />
        <ClientSelect
          value={clientId}
          placeholder={t('shipments.clientPlaceholder')}
          style={{ width: mobile ? '100%' : 260 }}
          onChange={(id) => {
            table.setFilter('client_id', id === null ? null : String(id));
            table.setFilter('delivery_point_id', null); // точка — внутри клиента
          }}
        />
        <Select<number>
          allowClear
          style={{ width: mobile ? '100%' : 220 }}
          placeholder={t('shipments.pointPlaceholder')}
          disabled={clientId === null}
          value={pointId}
          options={(client.data?.delivery_points ?? []).map((p) => ({
            value: p.id,
            label: p.name,
          }))}
          onChange={(id: number | undefined) => {
            table.setFilter('delivery_point_id', id === undefined ? null : String(id));
          }}
        />
        {/* Телефон: подпись над полем — сбоку она рвалась по буквам («Отгружен / а»). */}
        <Space
          size={4}
          direction={mobile ? 'vertical' : 'horizontal'}
          style={mobile ? { width: '100%' } : {}}
        >
          <Typography.Text type="secondary">{t('shipments.created')}</Typography.Text>
          <DatePicker.RangePicker
            format={DATE}
            style={mobile ? { width: '100%' } : {}}
            value={range(f.created_from[0], f.created_to[0])}
            onChange={(v) => {
              setRange('created', v?.[0] && v[1] ? [v[0], v[1]] : null);
            }}
          />
        </Space>
        {/* Телефон: подпись над полем — сбоку она рвалась по буквам («Отгружен / а»). */}
        <Space
          size={4}
          direction={mobile ? 'vertical' : 'horizontal'}
          style={mobile ? { width: '100%' } : {}}
        >
          <Typography.Text type="secondary">{t('shipments.shipped')}</Typography.Text>
          <DatePicker.RangePicker
            format={DATE}
            style={mobile ? { width: '100%' } : {}}
            value={range(f.shipped_from[0], f.shipped_to[0])}
            onChange={(v) => {
              setRange('shipped', v?.[0] && v[1] ? [v[0], v[1]] : null);
            }}
          />
        </Space>
        <Button
          onClick={() => {
            setSearch('');
            table.reset();
          }}
        >
          {t('shipments.reset')}
        </Button>
      </Space>
      <Table<ShipmentListItem>
        rowKey="id"
        columns={mobile ? mobileColumns : columns}
        dataSource={list.data?.items}
        loading={list.isFetching}
        pagination={table.pagination(list.data?.total)}
        onChange={table.onTableChange}
        locale={{ emptyText: t('shipments.empty') }}
        onRow={(s) => ({
          onClick: () => {
            void navigate(String(s.id));
          },
          style: { cursor: 'pointer' },
        })}
      />
    </Card>
  );
}
